import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    Chip,
    Stack,
    TextField,
    MenuItem,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    alpha,
    useTheme,
    Pagination,
    Select,
    IconButton,
    FormControl,
    InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

import CareRequestList from '../components/crm/CareRequestList';
import careService from '../services/careService';
import masterDataService from '../services/masterDataService';
import residentService from '../services/residentService';
import { CareRequestDTO, CarePriority, CareStatus } from '../types/crm.types';
import { ResidentDTO } from '../types/resident.types';
import { useTenant } from '../contexts/TenantContext';
import { Autocomplete } from '@mui/material';
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { formatToLocalDateTime } from "../utils/dateUtils";
import { parseISO } from "date-fns";

import userService from '../services/userService';
import { UserDTO } from '../types/user.types';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';

const CareRequestPage: React.FC = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const [priorityOptions, setPriorityOptions] = useState<{ valueName: string, orderNumber: number }[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<{ valueName: string, orderNumber: number }[]>([]);
    const [statusOptions, setStatusOptions] = useState<{ valueName: string, orderNumber: number }[]>([]);
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState<CareRequestDTO[]>([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<CareRequestDTO | null>(null);
    const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    const [popupMessage, setPopupMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [isSubmittingCloseRequest, setIsSubmittingCloseRequest] = useState(false);
    const [showCloseRequestInput, setShowCloseRequestInput] = useState(false);
    const [closeRequestText, setCloseRequestText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [activeUsers, setActiveUsers] = useState<UserDTO[]>([]);
    const [activeUsersLoading, setActiveUsersLoading] = useState(false);

    // Filter state
    const [filterCategory, setFilterCategory] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Form state
    const { currentTenantId } = useTenant();
    const [residents, setResidents] = useState<ResidentDTO[]>([]);
    const [residentsLoading, setResidentsLoading] = useState(false);
    const [selectedResidentForForm, setSelectedResidentForForm] = useState<ResidentDTO | null>(null);

    const [newRequest, setNewRequest] = useState<Partial<CareRequestDTO>>({
        description: '',
        priority: '' as any,
        category: '',
        subcategory: '',
        status: CareStatus.NEW,
        residentId: '',
        slaDeadline: '',
        assignedTo: '',
    });

    // Load lookup options on mount so they're available for the filter dropdowns
    useEffect(() => {
        const loadLookups = async () => {
            if (priorityOptions.length > 0) return;
            try {
                const [priorities, categories, statuses] = await Promise.all([
                    masterDataService.getActiveLookupValuesByName('CarerequestPriority'),
                    masterDataService.getActiveLookupValuesByName('CarerequestCategory'),
                    masterDataService.getActiveLookupValuesByName('CarerequestStatus'),
                ]);
                setPriorityOptions(priorities);
                setCategoryOptions(categories);
                setStatusOptions(statuses.map((s: any) =>
                    s.valueName.toLowerCase() === 'pending' ? { ...s, valueName: 'In Progress' } : s
                ));
            } catch (err) {
                console.error("Failed to load lookup options", err);
            }
        };
        loadLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isCreateDialogOpen && !isEditMode) return;

        const loadActiveUsers = async () => {
            setActiveUsersLoading(true);
            try {
                const fetchedUsers = await userService.getActiveUsers();
                setActiveUsers(Array.isArray(fetchedUsers) ? fetchedUsers : []);
            } catch (err) {
                console.error("Failed to load active users:", err);
            } finally {
                setActiveUsersLoading(false);
            }
        };

        const loadResidents = async () => {
            if (!currentTenantId) return;
            setResidentsLoading(true);
            try {
                const response = await residentService.getActiveLookup(currentTenantId);
                setResidents(response || []);
            } catch (err) {
                console.error("Failed to load residents:", err);
            } finally {
                setResidentsLoading(false);
            }
        };

        loadActiveUsers();
        loadResidents();
    }, [isCreateDialogOpen, isEditMode, currentTenantId]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (popupMessage) {
            timeoutId = setTimeout(() => {
                setPopupMessage(null);
            }, 3000);
        }
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [popupMessage]);

    useEffect(() => {
        if (isCreateDialogOpen && !isEditMode) {
            setNewRequest({
                description: '',
                priority: '' as any,
                category: '',
                subcategory: '',
                status: CareStatus.NEW,
                residentId: '',
                slaDeadline: '',
                assignedTo: '',
            });
            setSelectedResidentForForm(null);
        }
    }, [isCreateDialogOpen, isEditMode]);

    const handleResidentSearch = (query: string) => {
        // Local filtering handled by Autocomplete
    };

    const searchAbortControllerRef = useRef<AbortController | null>(null);

    const fetchRequests = useCallback(async (query: string = '') => {
        setLoading(true);
        try {
            if (searchAbortControllerRef.current) {
                searchAbortControllerRef.current.abort();
            }
            searchAbortControllerRef.current = new AbortController();

            let allItems: CareRequestDTO[] = [];
            
            if (query.trim() !== '') {
                if (!currentTenantId) throw new Error('Facility ID is required');
                allItems = await careService.searchTickets(currentTenantId, query.trim(), residents, searchAbortControllerRef.current.signal);
            } else {
                allItems = await careService.getTickets(residents, currentTenantId || undefined, 0, 1000);
            }
            setRequests(allItems || []);
        } catch (error: any) {
            if (error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                return;
            }
            console.error('Failed to fetch care requests', error);
        } finally {
            if (!searchAbortControllerRef.current?.signal.aborted) {
                setLoading(false);
            }
        }
    }, [residents, currentTenantId]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchRequests(searchQuery);
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchRequests, searchQuery]);

    useEffect(() => {
        if (selectedRequest?.resolutionNote) {
            setCloseRequestText(selectedRequest.resolutionNote);
        } else if (selectedRequest?.closedRemark) {
            setCloseRequestText(selectedRequest.closedRemark);
        } else if (!showCloseRequestInput) {
            setCloseRequestText('');
        }
    }, [selectedRequest?.id, selectedRequest?.closedRemark, selectedRequest?.resolutionNote, showCloseRequestInput]);

    const handleSaveRequest = async () => {
        try {
            // Find current DB user ID from active users list
            const currentDbUser = activeUsers.find(u => u.keycloakId === user?.id || u.id === user?.id);
            const internalUserId = currentDbUser?.id || user?.id;

            if (isEditMode && selectedRequest?.id) {
                await careService.updateTicket(selectedRequest.id, {
                    ...newRequest,
                    requestedById: internalUserId,
                    residentId: newRequest.residentId || selectedRequest.residentId,
                    residentName: selectedResidentForForm ? `${selectedResidentForForm.firstName} ${selectedResidentForForm.lastName}` : selectedRequest.residentName,
                } as any);
                showSnackbar('Care request updated successfully!', 'success');
            } else {
                await careService.createTicket({
                    ...newRequest as any,
                    requestedById: internalUserId,
                    unitId: (selectedResidentForForm as any)?.unitId || (selectedResidentForForm as any)?.facilityId || 'unit-A',
                    residentName: selectedResidentForForm ? `${selectedResidentForForm.firstName} ${selectedResidentForForm.lastName}` : '',
                    isEscalated: false,
                });
                showSnackbar('Care request created successfully!', 'success');
            }
            setIsCreateDialogOpen(false);
            fetchRequests(searchQuery);
        } catch (error) {
            console.error('Failed to save request', error);
            showSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} care request`, 'error');
        }
    };

    const handleStatusUpdate = async (id: string, newStatus: CareStatus) => {
        const ticket = requests.find(t => t.id === id);
        if (ticket?.status === CareStatus.CLOSED) {
            showSnackbar('Cannot update status of a closed request', 'error');
            return;
        }
        // Safety guard: CLOSED status must only be applied via closed-request submit flow.
        if (newStatus === CareStatus.CLOSED) {
            handleStartCloseRequest();
            return;
        }
        try {
            // Closed request note UI should only appear for the Closed Request flow.
            setShowCloseRequestInput(false);
            setCloseRequestText('');
            await careService.updateTicketStatus(id, newStatus);
            setRequests(prev => prev.map(t => t.id === id ? { ...t, status: newStatus, updatedAt: new Date().toISOString() } : t));
            if (selectedRequest?.id === id) {
                setSelectedRequest((prev: CareRequestDTO | null) => prev ? { ...prev, status: newStatus } : null);
            }
            if (isDetailDialogOpen) {
                setPopupMessage({ message: `Status updated to ${newStatus.replace('_', ' ')}`, severity: 'success' });
            } else {
                showSnackbar(`Status updated to ${newStatus.replace('_', ' ')}`, 'success');
            }
        } catch (error) {
            console.error('Failed to update status', error);
            if (isDetailDialogOpen) {
                setPopupMessage({ message: 'Failed to update status', severity: 'error' });
            } else {
                showSnackbar(`Failed to update status`, 'error');
            }
        }
    };

    const handleAssign = async (id: string) => {
        const ticket = requests.find(t => t.id === id);
        if (ticket?.status === CareStatus.CLOSED) {
            showSnackbar('Cannot assign a closed request', 'error');
            return;
        }
        const currentUserName = user?.name || user?.username || 'Current User';
        const currentUserId = user?.id || '';

        try {
            setShowCloseRequestInput(false);
            setCloseRequestText('');
            // Persist assignment and status update in a single call
            // Using updateTicketStatus which now supports passing assignedTo in the payload
            await careService.updateTicketStatus(id, CareStatus.ASSIGNED, currentUserId);

            // Update local state for immediate visibility
            setRequests(prev => prev.map(t => t.id === id ? {
                ...t,
                status: CareStatus.ASSIGNED,
                assignedTo: currentUserId || currentUserName,
                assignedToName: currentUserName,
                updatedAt: new Date().toISOString()
            } : t));

            if (selectedRequest?.id === id) {
                setSelectedRequest((prev: CareRequestDTO | null) => prev ? {
                    ...prev,
                    status: CareStatus.ASSIGNED,
                    assignedTo: currentUserId || currentUserName,
                    assignedToName: currentUserName
                } : null);
            }

            if (isDetailDialogOpen) {
                setPopupMessage({ message: `Request assigned to ${currentUserName}`, severity: 'success' });
            } else {
                showSnackbar(`Request assigned to ${currentUserName}`, 'success');
            }
        } catch (error) {
            console.error('Failed to assign request', error);
            if (isDetailDialogOpen) {
                setPopupMessage({ message: 'Failed to assign request', severity: 'error' });
            } else {
                showSnackbar('Failed to assign request', 'error');
            }
        }
    };

    const handleStartCloseRequest = () => {
        if (!selectedRequest) return;
        setShowCloseRequestInput(true);
        setCloseRequestText(selectedRequest.resolutionNote || selectedRequest.closedRemark || '');
    };

    const handleCancelCloseRequest = () => {
        setShowCloseRequestInput(false);
        setCloseRequestText('');
    };

    const handleSubmitCloseRequest = async () => {
        if (!selectedRequest?.id) return;
        // If the request is already closed, do nothing — don't re-trigger status change
        if (selectedRequest.status === CareStatus.CLOSED) return;
        const trimmedRequestNote = closeRequestText.trim();
        if (!trimmedRequestNote) {
            showSnackbar('Please enter a note before closing the request.', 'error');
            return;
        }

        try {
            setIsSubmittingCloseRequest(true);
            await careService.updateTicketStatus(selectedRequest.id, CareStatus.CLOSED, undefined, trimmedRequestNote);

            setRequests(prev =>
                prev.map(t =>
                    t.id === selectedRequest.id
                        ? { ...t, status: CareStatus.CLOSED, closedRemark: trimmedRequestNote, resolutionNote: trimmedRequestNote, updatedAt: new Date().toISOString() }
                        : t
                )
            );
            setSelectedRequest(prev =>
                prev
                    ? { ...prev, status: CareStatus.CLOSED, closedRemark: trimmedRequestNote, resolutionNote: trimmedRequestNote, updatedAt: new Date().toISOString() }
                    : null
            );
            setShowCloseRequestInput(false);
            setCloseRequestText('');
            // Close the detail dialog and show a snackbar (same behaviour as status-button close)
            setIsDetailDialogOpen(false);
            setSelectedRequest(null);
            setPopupMessage(null);
            showSnackbar('Request marked as Close Request successfully.', 'success');
        } catch (error) {
            console.error('Failed to Close & Submit', error);
            if (isDetailDialogOpen) {
                setPopupMessage({ message: 'Failed to mark request as Close Request', severity: 'error' });
            } else {
                showSnackbar('Failed to mark request as Close Request', 'error');
            }
        } finally {
            setIsSubmittingCloseRequest(false);
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleResetAll = () => {
        setSearchQuery('');
        setFilterCategory('');
        setFilterPriority('');
        setFilterStatus('');
        setPage(1);
    };

    useEffect(() => {
        setPage(1);
    }, [searchQuery, filterCategory, filterPriority, filterStatus]);

    const filteredRequests = useMemo(() => {
        return requests.filter(req => {
            const matchesCategory = !filterCategory || req.category?.toUpperCase() === filterCategory.toUpperCase();
            const matchesPriority = !filterPriority || req.priority?.toUpperCase() === filterPriority.toUpperCase();
            const matchesStatus = !filterStatus || req.status?.toUpperCase() === filterStatus.toUpperCase();
            return matchesCategory && matchesPriority && matchesStatus;
        });
    }, [requests, filterCategory, filterPriority, filterStatus]);

    const paginatedRequests = useMemo(() => {
        const startIndex = (page - 1) * 15;
        return filteredRequests.slice(startIndex, startIndex + 15);
    }, [filteredRequests, page]);

    const totalPagesCalculated = Math.ceil(filteredRequests.length / 15) || 1;

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", pt: 3, pb: 4, px: 0 }}>
            <Box sx={{ mb: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexWrap: { xs: "wrap", sm: "nowrap" }, gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: { xs: 1, sm: 0 }, flex: 1, minWidth: 0 }}>
                    <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <MedicalInformationIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h5"
                            fontWeight="700"
                            sx={{ mb: 0.5, color: "#1A2B45", whiteSpace: { xs: "normal", sm: "nowrap", md: "normal" }, overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            Care Requests
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, fontWeight: 500, fontSize: "0.85rem", whiteSpace: "normal" }}>
                            Track and resolve resident needs, maintenance requests, and care alerts efficiently
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setIsEditMode(false);
                        setSelectedRequest(null);
                        setIsCreateDialogOpen(true);
                    }}
                    sx={{
                        borderRadius: '12px',
                        px: 3,
                        py: 1,
                        fontWeight: 'normal',
                        textTransform: 'none',
                        boxShadow: 'none',
                        bgcolor: '#48CAE4',
                        color: '#000000',
                        '&:hover': { bgcolor: '#3ab3cc' },
                        height: '40px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        ml: { sm: "auto" }
                    }}
                >
                    New Request
                </Button>
            </Box>

            {/* ── Filter Bar: Search and Dropdowns ─────────── */}
            <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }} className="no-print">
                <TextField
                    placeholder="Search by Request Id, Resident, Resident Need…"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchQuery('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: 1, minWidth: 250 }}
                />

                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                        label="Category"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value as string)}
                    >
                        <MenuItem value="">All Categories</MenuItem>
                        {categoryOptions.map((c) => (
                            <MenuItem key={c.valueName} value={c.valueName.toUpperCase()}>
                                {c.valueName.charAt(0).toUpperCase() + c.valueName.slice(1).toLowerCase()}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                        label="Priority"
                        value={filterPriority}
                        onChange={(e) => setFilterPriority(e.target.value as string)}
                    >
                        <MenuItem value="">All Priorities</MenuItem>
                        {priorityOptions.map((p) => (
                            <MenuItem key={p.valueName} value={p.valueName.toUpperCase()}>
                                {p.valueName.charAt(0).toUpperCase() + p.valueName.slice(1).toLowerCase()}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as string)}
                    >
                        <MenuItem value="">All Statuses</MenuItem>
                        {statusOptions.map((s) => (
                            <MenuItem key={s.valueName} value={s.valueName.toUpperCase().replace(/ /g, '_')}>
                                {s.valueName.split(/_| /).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Tooltip title="Reset filters">
                    <IconButton onClick={handleResetAll} sx={{ my: 'auto', ml: -1 }}>
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>
            </Paper>

            {(loading && requests.length === 0) ? (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress />
                </Box>
            ) : (
                <CareRequestList
                    tickets={paginatedRequests}
                    userRole={user?.role}
                    onView={async (t: CareRequestDTO) => {
                        setShowCloseRequestInput(false);
                        setCloseRequestText('');
                        setSelectedRequest(t);
                        setIsDetailDialogOpen(true);
                        setLoadingDetail(true);
                        try {
                            const fullTicket = await careService.getTicketById(t.id, residents);
                            setSelectedRequest(fullTicket);
                        } catch (error) {
                            console.error('Failed to fetch ticket details', error);
                        } finally {
                            setLoadingDetail(false);
                        }
                    }}
                    onEdit={(t: CareRequestDTO) => {
                        setSelectedRequest(t);
                        setNewRequest({
                            description: t.description,
                            priority: t.priority,
                            category: t.category?.toUpperCase(),
                            subcategory: t.subcategory,
                            status: t.status,
                            residentId: t.residentId,
                            slaDeadline: t.slaDeadline,
                            assignedTo: t.assignedTo || ''
                        });

                        // Use a fallback if the resident isn't in the current local residents list
                        const res = residents.find(r => r.id === t.residentId);
                        if (res) {
                            setSelectedResidentForForm(res);
                        } else if (t.residentId) {
                            // Create a minimal placeholder so the Autocomplete shows the name
                            setSelectedResidentForForm({
                                id: t.residentId,
                                firstName: t.residentName?.split(' ')[0] || 'Unknown',
                                lastName: t.residentName?.split(' ').slice(1).join(' ') || '',
                                status: "ACTIVE" as any, // Assume active if they have a request
                            } as any);
                        }

                        setIsEditMode(true);
                        setIsCreateDialogOpen(true);
                    }}
                />
            )}

            {/* Pagination */}
            {!loading && filteredRequests.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'center' }, gap: 2, mt: 3 }} className="no-print">
                    <Typography variant="body2" color="text.secondary">
                        Showing {paginatedRequests.length > 0 ? (page - 1) * 15 + 1 : 0} to {Math.min(page * 15, filteredRequests.length)} of {filteredRequests.length} entries
                    </Typography>
                    <Pagination
                        count={totalPagesCalculated}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        variant="outlined"
                        shape="rounded"
                        size="small"
                    />
                </Box>
            )}

            {/* Create Request Dialog */}
            <Dialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
                disableScrollLock
            >
                <DialogTitle sx={{ fontWeight: '900', fontSize: '1.5rem' }}>
                    {isEditMode ? 'Edit Care Request' : 'Log New Care Request'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, "& .MuiFormLabel-asterisk": { color: "error.main" } }}>
                        <Grid container spacing={2}>
                            {/* Select Resident */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    disabled={isEditMode}
                                    options={
                                        [
                                            ...residents.filter(r => r?.status?.toUpperCase() === "ACTIVE"),
                                            ...(selectedResidentForForm && !residents.find(r => r.id === selectedResidentForForm.id) ? [selectedResidentForForm] : [])
                                        ].sort((a: any, b: any) => {
                                            const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
                                            const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
                                            return nameA.localeCompare(nameB);
                                        })
                                    }
                                    loading={residentsLoading}
                                    value={selectedResidentForForm}
                                    getOptionLabel={(option: ResidentDTO) =>
                                        `${(option as any).residentName || `${option.firstName} ${option.lastName}`} ${option.roomNumber ? `(Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : ""}`
                                    }
                                    onInputChange={(_, value, reason) => {
                                        if (reason === 'input') {
                                            handleResidentSearch(value);
                                        }
                                    }}
                                    onChange={(_, value) => {
                                        setSelectedResidentForForm(value);
                                        setNewRequest({ ...newRequest, residentId: value?.id || '' });
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="Select Resident"
                                            placeholder="Search Resident by Name"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {residentsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Resident Need */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Resident Need"
                                    value={newRequest.subcategory}
                                    onChange={(e) => setNewRequest({ ...newRequest, subcategory: e.target.value })}
                                />
                            </Grid>

                            {/* Priority Dropdown */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    required
                                    label="Priority"
                                    value={newRequest.priority}
                                    onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value as CarePriority })}
                                >
                                    {priorityOptions.map((p) => (
                                        <MenuItem key={p.valueName} value={p.valueName.toUpperCase()}>
                                            {p.valueName.charAt(0).toUpperCase() + p.valueName.slice(1).toLowerCase()}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Category Dropdown */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    required
                                    label="Category"
                                    value={newRequest.category?.toUpperCase() || ''}
                                    onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                                >
                                    {categoryOptions.map((c) => (
                                        <MenuItem key={c.valueName} value={c.valueName.toUpperCase()}>
                                            {c.valueName.charAt(0).toUpperCase() + c.valueName.slice(1).toLowerCase()}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Status Dropdown */}
                            <Grid item xs={12} sm={newRequest.status === 'ASSIGNED' ? 4 : 6}>
                                <TextField
                                    fullWidth
                                    select
                                    required
                                    label="Status"
                                    value={newRequest.status}
                                    disabled={selectedRequest?.status === CareStatus.CLOSED}
                                    onChange={(e) => setNewRequest({ ...newRequest, status: e.target.value as CareStatus })}
                                >
                                    {statusOptions.map((s) => (
                                        <MenuItem key={s.valueName} value={s.valueName.toUpperCase().replace(/ /g, '_')}>
                                            {s.valueName.split(/_| /).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Assigned To Dropdown (Conditional) */}
                            {newRequest.status === 'ASSIGNED' && (
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        required
                                        label="Select User"
                                        value={newRequest.assignedTo || ''}
                                        onChange={(e) => setNewRequest({ ...newRequest, assignedTo: e.target.value })}
                                        disabled={activeUsersLoading}
                                        SelectProps={{
                                            MenuProps: {
                                                PaperProps: { style: { maxHeight: 250 } },
                                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                transformOrigin: { vertical: 'top', horizontal: 'left' }
                                            }
                                        }}
                                    >
                                        {activeUsers.map((u) => {
                                            const displayName = u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim();
                                            return (
                                                <MenuItem key={u.id} value={u.keycloakId || u.id}>
                                                    {displayName}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Grid>
                            )}

                            {/* Response Deadline */}
                            <Grid item xs={12} sm={newRequest.status === 'ASSIGNED' ? 4 : 6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Response Deadline "
                                        format="dd/MM/yyyy"
                                        disablePast
                                        value={newRequest.slaDeadline ? parseISO(newRequest.slaDeadline) : null}
                                        onChange={(date) => {
                                            if (date) {
                                                setNewRequest({ ...newRequest, slaDeadline: formatToLocalDateTime(date) || '' });
                                            } else {
                                                setNewRequest({ ...newRequest, slaDeadline: '' });
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                                InputLabelProps: { shrink: true }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* Description Field */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={4}
                                    value={newRequest.description}
                                    onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setIsCreateDialogOpen(false)} color="inherit" sx={{ fontWeight: '700' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveRequest}
                        disabled={!newRequest.residentId || !newRequest.subcategory || !newRequest.priority || !newRequest.category || !newRequest.status || !newRequest.slaDeadline || (newRequest.status === 'ASSIGNED' && !newRequest.assignedTo)}
                        sx={{ borderRadius: '10px', fontWeight: 'normal', px: 3, boxShadow: 'none', bgcolor: '#48CAE4', color: '#000000', '&:hover': { bgcolor: '#3ab3cc' } }}
                    >
                        {isEditMode ? 'Save Changes' : 'Create Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Request Detail Dialog */}
            <Dialog
                open={isDetailDialogOpen}
                onClose={() => {
                    setIsDetailDialogOpen(false);
                    setSelectedRequest(null);
                    setShowCloseRequestInput(false);
                    setCloseRequestText('');
                    setPopupMessage(null);
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, bgcolor: 'background.default' } }}
                disableScrollLock
            >
                {selectedRequest && (
                    <>
                        <DialogTitle sx={{ p: 4, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="caption" fontWeight="800" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                                    {selectedRequest.requestNumber}
                                </Typography>
                                <Typography variant="h5" fontWeight="800">Care Request Details</Typography>
                            </Box>
                            <Chip
                                label={selectedRequest.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                                color={
                                    ({
                                        NEW: 'primary',
                                        PENDING: 'warning',
                                        ASSIGNED: 'info',
                                        IN_PROGRESS: 'warning',
                                        HOLD: 'secondary',
                                        RESOLVED: 'success',
                                        CLOSED: 'error'
                                    } as any)[selectedRequest.status] || 'default'
                                }
                                variant="outlined"
                                sx={{
                                    fontWeight: '800',
                                    borderRadius: 2,
                                    borderWidth: '2px',
                                    bgcolor: (() => {
                                        const colorKey = ({
                                            NEW: 'primary', PENDING: 'warning', ASSIGNED: 'info', IN_PROGRESS: 'warning', HOLD: 'secondary', RESOLVED: 'success', CLOSED: 'error'
                                        } as Record<string, string>)[selectedRequest.status] || 'default';
                                        const paletteColor = (theme.palette as any)[colorKey === 'default' ? 'grey' : colorKey];
                                        return alpha(paletteColor?.main || paletteColor?.[500] || theme.palette.primary.main, 0.1);
                                    })()
                                }}
                            />
                        </DialogTitle>
                        <DialogContent sx={{ p: 4 }}>
                            {popupMessage && (
                                <Alert severity={popupMessage.severity} sx={{ mb: 3 }}>
                                    {popupMessage.message}
                                </Alert>
                            )}
                            {loadingDetail ? (
                                <Box display="flex" justifyContent="center" py={10}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={7}>
                                        <Stack spacing={2} sx={{ mb: 3 }}>
                                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="subtitle2" color="text.secondary" fontWeight="700" gutterBottom>
                                                    Resident Name
                                                </Typography>
                                                <Typography variant="body1">
                                                    {selectedRequest.residentName}
                                                </Typography>
                                            </Paper>

                                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="subtitle2" color="text.secondary" fontWeight="700" gutterBottom>
                                                    Resident Need
                                                </Typography>
                                                <Typography variant="body1">
                                                    {selectedRequest.subcategory}
                                                </Typography>
                                            </Paper>

                                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="subtitle2" color="text.secondary" fontWeight="700" gutterBottom>
                                                    Description
                                                </Typography>
                                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {selectedRequest.description}
                                                </Typography>
                                            </Paper>

                                            {(showCloseRequestInput || (selectedRequest.status === CareStatus.CLOSED && (!!selectedRequest.resolutionNote || !!selectedRequest.closedRemark))) && (
                                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="700" gutterBottom>
                                                        Note <span style={{ color: theme.palette.error.main }}>*</span>
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        required
                                                        minRows={3}
                                                        placeholder="Enter close request note..."
                                                        value={closeRequestText}
                                                        onChange={(e) => setCloseRequestText(e.target.value)}
                                                        disabled={selectedRequest.status === CareStatus.CLOSED || isSubmittingCloseRequest}
                                                    />
                                                    {selectedRequest.status !== CareStatus.CLOSED && showCloseRequestInput && (
                                                        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                            <Button
                                                                variant="text"
                                                                color="inherit"
                                                                onClick={handleCancelCloseRequest}
                                                                disabled={isSubmittingCloseRequest}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleSubmitCloseRequest}
                                                                disabled={!closeRequestText.trim() || isSubmittingCloseRequest}
                                                            >
                                                                {isSubmittingCloseRequest ? <CircularProgress size={18} color="inherit" /> : 'Close & Submit'}
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            )}
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Stack spacing={2}>
                                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <CategoryIcon color="action" />
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary" fontWeight="700" display="block">Category</Typography>
                                                        <Typography variant="body1">{selectedRequest.category}</Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <AccessTimeIcon color="action" />
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary" fontWeight="700" display="block">Response Deadline</Typography>
                                                        <Typography variant="body1">{new Date(selectedRequest.slaDeadline).toLocaleDateString('en-GB')}</Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <PersonIcon color="action" />
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary" fontWeight="700" display="block">Staff Assigned</Typography>
                                                        <Typography variant="body1">
                                                            {(() => {
                                                                if (selectedRequest.assignedToName && selectedRequest.assignedToName.toLowerCase() !== 'system') {
                                                                    return selectedRequest.assignedToName;
                                                                }
                                                                if (selectedRequest.assignedTo) {
                                                                    const activeStaff = activeUsers.find(u => u.id === selectedRequest.assignedTo || u.keycloakId === selectedRequest.assignedTo);
                                                                    if (activeStaff) {
                                                                        return activeStaff.fullName || `${activeStaff.firstName || ''} ${activeStaff.lastName || ''}`.trim() || activeStaff.username;
                                                                    }
                                                                }
                                                                return 'Unassigned';
                                                            })()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>

                                            <Typography variant="subtitle2" fontWeight="700" sx={{ px: 1 }}>Manage Interventions</Typography>
                                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Grid container spacing={1}>
                                                    {selectedRequest.status !== CareStatus.IN_PROGRESS && (
                                                        <Grid item xs={12}>
                                                            <Button
                                                                fullWidth
                                                                variant="outlined"
                                                                color="info"
                                                                startIcon={<PlayCircleOutlineIcon />}
                                                                onClick={() => handleStatusUpdate(selectedRequest.id, CareStatus.IN_PROGRESS)}
                                                                disabled={selectedRequest.status === CareStatus.CLOSED}
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                In Progress
                                                            </Button>
                                                        </Grid>
                                                    )}
                                                    {!selectedRequest.assignedTo && (
                                                        <Grid item xs={12}>
                                                            <Button
                                                                fullWidth
                                                                variant="contained"
                                                                startIcon={<AssignmentIndIcon />}
                                                                onClick={() => handleAssign(selectedRequest.id)}
                                                                disabled={selectedRequest.status === CareStatus.CLOSED}
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                Self Assign
                                                            </Button>
                                                        </Grid>
                                                    )}
                                                    <Grid item xs={6}>
                                                        <Button
                                                            fullWidth
                                                            variant={selectedRequest.status === CareStatus.HOLD ? 'contained' : 'outlined'}
                                                            color="secondary"
                                                            startIcon={<PauseCircleIcon />}
                                                            onClick={() => handleStatusUpdate(selectedRequest.id, CareStatus.HOLD)}
                                                            disabled={selectedRequest.status === CareStatus.CLOSED}
                                                            sx={{
                                                                borderRadius: 2,
                                                                ...(selectedRequest.status === CareStatus.HOLD ? {
                                                                    bgcolor: theme.palette.secondary.main,
                                                                    color: '#fff',
                                                                    '&:hover': {
                                                                        bgcolor: theme.palette.secondary.dark,
                                                                    }
                                                                } : {
                                                                    borderColor: theme.palette.secondary.main,
                                                                    color: theme.palette.secondary.main,
                                                                    '&:hover': {
                                                                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                                                        borderColor: theme.palette.secondary.dark,
                                                                    }
                                                                })
                                                            }}
                                                        >
                                                            Hold
                                                        </Button>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Button
                                                            fullWidth
                                                            variant={selectedRequest.status === CareStatus.RESOLVED ? 'contained' : 'outlined'}
                                                            color="success"
                                                            startIcon={<CheckCircleIcon />}
                                                            onClick={() => handleStatusUpdate(selectedRequest.id, CareStatus.RESOLVED)}
                                                            disabled={selectedRequest.status === CareStatus.CLOSED}
                                                            sx={{
                                                                borderRadius: 2,
                                                                ...(selectedRequest.status === CareStatus.RESOLVED ? {
                                                                    bgcolor: theme.palette.success.main,
                                                                    color: '#fff',
                                                                    '&:hover': {
                                                                        bgcolor: theme.palette.success.dark,
                                                                    }
                                                                } : {
                                                                    borderColor: theme.palette.success.main,
                                                                    color: theme.palette.success.main,
                                                                    '&:hover': {
                                                                        bgcolor: alpha(theme.palette.success.main, 0.08),
                                                                        borderColor: theme.palette.success.dark,
                                                                    }
                                                                })
                                                            }}
                                                        >
                                                            Resolve
                                                        </Button>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Button
                                                            fullWidth
                                                            variant={selectedRequest.status === CareStatus.CLOSED ? 'contained' : 'outlined'}
                                                            color="error"
                                                            startIcon={<CancelIcon />}
                                                            onClick={handleStartCloseRequest}
                                                            disabled={selectedRequest.status === CareStatus.CLOSED}
                                                            sx={{
                                                                borderRadius: 2,
                                                                ...(selectedRequest.status === CareStatus.CLOSED ? {
                                                                    bgcolor: theme.palette.error.main,
                                                                    color: '#fff',
                                                                    '&:hover': {
                                                                        bgcolor: theme.palette.error.dark,
                                                                    }
                                                                } : {
                                                                    borderColor: theme.palette.error.main,
                                                                    color: theme.palette.error.main,
                                                                    '&:hover': {
                                                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                                                        borderColor: theme.palette.error.dark,
                                                                    }
                                                                })
                                                            }}
                                                        >
                                                            Close Request
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 4, pt: 0 }}>
                            <Button onClick={() => setIsDetailDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CareRequestPage;
